import { Checkbox, Grid } from '@mui/material';
import moment from 'moment';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplicationConfig } from '~/data/applicationConfig';
import { EmploymentData } from '~/data/employee';
import { Enum } from '~/data/enums';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import { EmploymentTypeComponent } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/components/FormComponent/components/EmploymentTypeComponent';
import {
  Form,
  Restrict,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import {
  LegacyAutocompleteField,
  LegacyDateField,
  LegacyFieldWrapper,
  LegacyInputField,
  LegacyMuiButton,
} from '~/legacy-components/LegacyForms';

import {
  FixedTermReasonComponent,
  TerminationReasonComponent,
} from './components';
import { useSxStyles } from './useStyles';

type Props = {
  terminationReasons: Enum[];
  fixedTermReasons: Enum[];
  trialPeriodOptions: Enum[];
  employmentTypeOptions: Enum[];
  employmentData: EmploymentData;
  loading: boolean;
  scheduleFormMethods: UseFormReturn<Form>;
  restrictField: Restrict;
  index: number;
  newEmpl: boolean;
  hasWorkEmail: boolean;
};

export const FormComponent = (props: Props) => {
  const {
    scheduleFormMethods,
    employmentData,
    trialPeriodOptions,
    fixedTermReasons,
    terminationReasons,
    employmentTypeOptions,
    restrictField,
    index,
    newEmpl,
    hasWorkEmail,
  } = props;

  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = scheduleFormMethods;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { applicationConfig } = useApplicationConfig();
  const enableManagerEditEmploymentStartDate =
    applicationConfig.fComConfig.mgrConfig.fComEnableMgrEditEmpStartDate;

  const isCurrentEmployment = index === 0;

  const disableTerminationFields =
    isCurrentEmployment && restrictField.notAllowEmpTermDate;

  const hasOnlyManagerAccess =
    restrictField.manager && !restrictField.inMyHRUnit;
  const disableStartAndArrivalDateFields =
    isCurrentEmployment &&
    hasOnlyManagerAccess &&
    !enableManagerEditEmploymentStartDate;

  const TerminationFields = () => {
    // @ts-ignore
    const endOfEmploymentField = useWatch({
      control,
      name: `schedule.${index}.employment.fEmplLastDateEmployment`,
    });

    return (
      <>
        <Grid item>
          <Controller
            name={`schedule.${index}.employment.fEmplTerminationDate`}
            control={control}
            rules={{ required: !!endOfEmploymentField }}
            render={({ field: { onChange, value } }) => (
              <LegacyDateField
                disabled={disableTerminationFields}
                minDate={
                  index !== 0
                    ? moment(
                        getValues(
                          `schedule.${
                            index - 1
                          }.employment.fEmplLastDateEmployment`,
                        ),
                      ).add(1, 'day')
                    : undefined
                }
                label={t('fLastDTemp')}
                onChange={(v: moment.Moment | null) => onChange(v || null)}
                value={value || null}
                required={!!endOfEmploymentField}
                maxDate={
                  endOfEmploymentField ? endOfEmploymentField : undefined
                }
              />
            )}
          />
        </Grid>
        <Grid item>
          <TerminationReasonComponent
            control={control}
            index={index}
            terminationReasons={terminationReasons}
            getValues={getValues}
            disabled={disableTerminationFields}
            errors={errors}
            endOfEmploymentField={endOfEmploymentField}
          />
        </Grid>
      </>
    );
  };

  const ArrivalDateField = () => {
    const empStartDate = useWatch({
      control,
      name: `schedule.${index}.employment.fEmplDateStart`,
    });

    return (
      <Grid item>
        <Controller
          name={`schedule.${index}.employment.fEmplDateArrival`}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <LegacyDateField
              disabled={disableStartAndArrivalDateFields}
              minDate={empStartDate || undefined}
              value={value || null}
              onChange={(v: moment.Moment | null) => {
                onChange(v || null);
              }}
              label={t('fArrivalDate')}
              required
              fieldError={
                errors.schedule?.[index]?.employment?.fEmplDateArrival
              }
            />
          )}
        />
      </Grid>
    );
  };

  const TrialPeriodEndsField = () => {
    const trialPeriod = useWatch({
      control,
      name: `schedule.${index}.employment.fEmplTrialPeriod`,
    });
    const trialPeriodHasValue = !!trialPeriod;
    return (
      <Grid item>
        <Controller
          name={`schedule.${index}.employment.fEmplTrialPeriodEndDate`}
          control={control}
          rules={{ required: trialPeriodHasValue }}
          render={({ field: { onChange, value } }) => (
            <LegacyDateField
              label={t('fTrialPeriodEndDateInputLabel')}
              onChange={(v: moment.Moment | null) => onChange(v || null)}
              value={value || null}
              required={trialPeriodHasValue}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <Grid container columnSpacing={1}>
      <Grid item>
        <EmploymentTypeComponent
          control={control}
          getValues={getValues}
          index={index}
          employmentTypeOptions={employmentTypeOptions}
        />
      </Grid>
      {index === 0 && (
        <>
          <Grid item>
            <Controller
              control={control}
              name={`schedule.${index}.fEmpContractNumber`}
              render={({ field: { onChange, value } }) => (
                <LegacyInputField
                  disabled={index !== 0}
                  value={value}
                  sx={sxStyles.selectStyle}
                  onChange={onChange}
                  label={t('fEmpContractNumber')}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name={`schedule.${index}.mainContract`}
              render={({ field: { onChange, value } }) => (
                <LegacyFieldWrapper
                  label={t('fEmpMainContract')}
                  help={
                    value === false && !hasWorkEmail
                      ? {
                          title: '',
                          content: t('mainContractInstructions'),
                        }
                      : undefined
                  }
                >
                  <Checkbox
                    sx={{
                      padding: 0,
                    }}
                    disabled={!hasWorkEmail || value === true}
                    checked={value || false}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                </LegacyFieldWrapper>
              )}
            />
          </Grid>
        </>
      )}
      {index === 0 && (
        <Grid item>
          <Controller
            control={control}
            name={`schedule.${index}.fEmpPersonNumber`}
            render={({ field: { onChange, value } }) => (
              <LegacyInputField
                disabled={restrictField.manager && !restrictField.inMyHRUnit}
                value={value}
                sx={sxStyles.selectStyle}
                onChange={onChange}
                label={t('fEmpPersonNumber')}
              />
            )}
          />
        </Grid>
      )}

      <Grid item>
        <Controller
          name={`schedule.${index}.employment.fEmplDateStart`}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <LegacyDateField
              disabled={disableStartAndArrivalDateFields}
              value={value || null}
              minDate={
                index !== 0
                  ? moment(
                      getValues(
                        `schedule.${
                          index - 1
                        }.employment.fEmplLastDateEmployment`,
                      ),
                    ).add(1, 'day')
                  : undefined
              }
              onChange={(v: moment.Moment | null) => {
                if (v) {
                  const empTrialPeriodValue = getValues(
                    `schedule.${index}.employment.fEmplTrialPeriod`,
                  );
                  if (empTrialPeriodValue) {
                    setValue(
                      `schedule.${index}.employment.fEmplTrialPeriodEndDate`,
                      moment(v, scheduledDateFormats.dotFormat)
                        .add(parseInt(empTrialPeriodValue.charAt(1)), 'months')
                        .subtract(1, 'days'),
                    );
                  } else {
                    setValue(
                      `schedule.${index}.employment.fEmplTrialPeriodEndDate`,
                      null,
                    );
                  }
                  const detailsStartValue = getValues(
                    `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
                  );
                  const positioStartValue = getValues(
                    `schedule.${index}.empPosition.fInternalPositionStartDate`,
                  );
                  if (newEmpl) {
                    setValue(
                      `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
                      v,
                    );
                    setValue(
                      `schedule.${index}.empPosition.fInternalPositionStartDate`,
                      v,
                    );
                  } else {
                    //INFO: can't schedule new changes before the empl starts, that's why this sets the others to the same value if before they had earlier values
                    if (v.isAfter(positioStartValue)) {
                      setValue(
                        `schedule.${index}.empPosition.fInternalPositionStartDate`,
                        v,
                      );
                    }
                    if (v.isAfter(detailsStartValue)) {
                      setValue(
                        `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
                        v,
                      );
                    }
                  }
                }
                const dateArrivalValue = getValues(
                  `schedule.${index}.employment.fEmplDateArrival`,
                );
                if (!v || v.isAfter(dateArrivalValue)) {
                  setValue(
                    `schedule.${index}.employment.fEmplDateArrival`,
                    v || null,
                  );
                }
                onChange(v || null);
              }}
              label={t('fEmplDateStart')}
              required
              fieldError={errors.schedule?.[index]?.employment?.fEmplDateStart}
            />
          )}
        />
      </Grid>
      <ArrivalDateField />
      <Grid item>
        <Controller
          control={control}
          name={`schedule.${index}.employment.fEmplTrialPeriod`}
          render={({ field: { onChange, value } }) => (
            <LegacyAutocompleteField
              options={trialPeriodOptions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              onChange={(_, value) => {
                if (value) {
                  const empStartDateValue = getValues(
                    `schedule.${index}.employment.fEmplDateStart`,
                  );
                  setValue(
                    `schedule.${index}.employment.fEmplTrialPeriodEndDate`,
                    moment(empStartDateValue, scheduledDateFormats.dotFormat)
                      .add(parseInt(value.code.charAt(1)), 'months')
                      .subtract(1, 'days'),
                  );
                } else {
                  setValue(
                    `schedule.${index}.employment.fEmplTrialPeriodEndDate`,
                    null,
                  );
                }
                onChange(value?.code || '');
              }}
              value={trialPeriodOptions.find((o) => o.code === value) || null}
              sx={sxStyles.selectStyle}
              label={t('fTrialPeriod')}
            />
          )}
        />
      </Grid>
      <TrialPeriodEndsField />
      <Grid item>
        <Controller
          name={`schedule.${index}.employment.fEmplPlannedEndDate`}
          control={control}
          render={({ field: { onChange, value } }) => (
            <LegacyDateField
              minDate={
                index !== 0
                  ? moment(
                      getValues(
                        `schedule.${
                          index - 1
                        }.employment.fEmplLastDateEmployment`,
                      ),
                    ).add(1, 'day')
                  : undefined
              }
              label={t('fEmplPlannedEndDate')}
              onChange={(v: moment.Moment | null) => onChange(v || null)}
              value={value || null}
            />
          )}
        />
      </Grid>
      <Grid item container sx={{ width: 'fit-content' }}>
        <Grid item>
          <Controller
            name={`schedule.${index}.employment.fEmplLastDateEmployment`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <LegacyDateField
                disabled={disableTerminationFields}
                minDate={
                  moment(
                    getValues(`schedule.${index}.employment.fEmplDateStart`),
                  ) || undefined
                }
                maxDate={
                  getValues(`schedule.${index + 1}.employment.fEmplDateStart`)
                    ? moment(
                        getValues(
                          `schedule.${index + 1}.employment.fEmplDateStart`,
                        ),
                      ).subtract(1, 'day')
                    : undefined
                }
                label={t('fLastDEmpl')}
                onChange={(v: moment.Moment | null) => {
                  onChange(v || null);
                  setValue(
                    `schedule.${index}.employment.fEmplTerminationDate`,
                    v || null,
                  );
                  if (v) {
                    const detailsStartValue = getValues(
                      `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
                    );
                    const positioStartValue = getValues(
                      `schedule.${index}.empPosition.fInternalPositionStartDate`,
                    );
                    //INFO: can't schedule new changes after the empl ends, that's why this sets the others to the same value if before they had later values
                    if (v.isBefore(positioStartValue)) {
                      setValue(
                        `schedule.${index}.empPosition.fInternalPositionStartDate`,
                        v,
                      );
                    }
                    if (v.isBefore(detailsStartValue)) {
                      setValue(
                        `schedule.${index}.employmentDetails.fEmplDetailStartDate`,
                        v,
                      );
                    }
                  }
                }}
                value={value || null}
              />
            )}
          />
        </Grid>
        {!restrictField.notAllowEmpTermDate && (
          <Grid item sx={{ paddingTop: 3.5 }}>
            <LegacyMuiButton
              onClick={() => {
                setValue(
                  `schedule.${index}.employment.fEmplLastDateEmployment`,
                  null,
                  { shouldDirty: true },
                );
                setValue(
                  `schedule.${index}.employment.fEmplTerminationDate`,
                  null,
                  { shouldDirty: true },
                );
              }}
              startIcon={<i className="fa fa-undo"></i>}
            >
              {t('laClear')}
            </LegacyMuiButton>
          </Grid>
        )}
      </Grid>
      <TerminationFields />
      <Grid item>
        <FixedTermReasonComponent
          control={control}
          index={index}
          fixedTermReasons={fixedTermReasons}
          getValues={getValues}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name={`schedule.${index}.employment.fEmplInfo`}
          defaultValue={employmentData.fEmplInfo}
          render={({ field: { onChange, value } }) => (
            <LegacyInputField
              inputProps={{ maxLength: 3000 }}
              fullWidth
              multiline
              value={value}
              onChange={onChange}
              label={t('laInfo')}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
